<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Please provide the legal information for this entity account, including
          the entity's full legal name, tax identification, and formation details.
        </p>
      </div>
    </help-drawer>
    <v-form name="EntityInfo" class="form" data-test-id="entity-info-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.ENTITY_INFO.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="entity-info-header">
              <span class="headerlight">
                Provide <strong>entity information</strong> for
                <strong>{{ label }}</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving entity information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isEntityInfoComplete && formHidden && !isSyncing"
          :submitted="isEntityInfoComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-row class="justify-center" v-if="!formHidden && !isSyncing">
          <v-col class="step-form-container large">
            
            <!-- Entity Legal Name -->
            <label for="fullLegalName" class="required" data-test-id="full-legal-name-label">
              Full Legal Name of Entity
            </label>
            <v-text-field
              outlined
              type="text"
              maxlength="256"
              name="fullLegalName"
              aria-required="true"
              v-model.trim="entityInfo.fullLegalName"
              :backgroundColor="inputBackgroundColor"
              @input="$v.entityInfo.fullLegalName.$touch()"
              @blur="$v.entityInfo.fullLegalName.$touch()"
              :error-messages="fullLegalNameErrors"
              hide-details="auto"
              class="mb-4"
              data-test-id="full-legal-name-input"
            />
            
            <!-- Tax Information -->
            <h3 class="mb-4">Tax Information</h3>
            <v-row>
              <v-col cols="12" sm="6">
                <label for="taxIdType" class="required" data-test-id="tax-id-type-label">
                  Tax ID Type
                </label>
                <v-select
                  v-model="entityInfo.taxIdType"
                  :items="taxIdTypes"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.entityInfo.taxIdType.$touch()"
                  :error-messages="taxIdTypeErrors"
                  data-test-id="tax-id-type-select"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <label for="taxId" class="required" data-test-id="tax-id-label">
                  Tax ID Number
                </label>
                <v-text-field
                  outlined
                  type="text"
                  :maxlength="entityInfo.taxIdType === 'SSN' ? 11 : 10"
                  name="taxId"
                  aria-required="true"
                  v-model="entityInfo.taxId"
                  :placeholder="entityInfo.taxIdType === 'SSN' ? '###-##-####' : '##-#######'"
                  :backgroundColor="inputBackgroundColor"
                  @input="formatTaxId"
                  @blur="$v.entityInfo.taxId.$touch()"
                  :error-messages="taxIdErrors"
                  hide-details="auto"
                  data-test-id="tax-id-input"
                />
              </v-col>
            </v-row>
            
            <v-row class="mt-2">
              <v-col cols="12" sm="6">
                <label for="taxClassification" class="required" data-test-id="tax-classification-label">
                  Tax Classification
                </label>
                <v-select
                  v-model="entityInfo.taxClassification"
                  :items="taxClassifications"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.entityInfo.taxClassification.$touch()"
                  :error-messages="taxClassificationErrors"
                  data-test-id="tax-classification-select"
                />
              </v-col>
            </v-row>
            
            <!-- Contact Information -->
            <h3 class="mt-6 mb-4">Contact Information</h3>
            <v-row>
              <v-col cols="12" sm="6">
                <label for="email" class="required" data-test-id="email-label">
                  Email
                </label>
                <v-text-field
                  outlined
                  type="email"
                  maxlength="256"
                  name="email"
                  aria-required="true"
                  v-model.trim="entityInfo.email"
                  :backgroundColor="inputBackgroundColor"
                  @input="$v.entityInfo.email.$touch()"
                  @blur="$v.entityInfo.email.$touch()"
                  :error-messages="emailErrors"
                  hide-details="auto"
                  data-test-id="email-input"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <label for="phone" data-test-id="phone-label">
                  Phone (Optional)
                </label>
                <v-text-field
                  outlined
                  type="tel"
                  maxlength="14"
                  name="phone"
                  v-model="entityInfo.phone"
                  placeholder="###-###-####"
                  :backgroundColor="inputBackgroundColor"
                  @input="formatPhone"
                  hide-details="auto"
                  data-test-id="phone-input"
                />
              </v-col>
            </v-row>
            
            <!-- Formation Details -->
            <h3 class="mt-6 mb-4">Formation Details</h3>
            
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <label for="stateOfOrganization" class="required" data-test-id="state-org-label">
                  State of Organization
                </label>
                <v-select
                  v-model="entityInfo.stateOfOrganization"
                  :items="states"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.entityInfo.stateOfOrganization.$touch()"
                  :error-messages="stateOfOrganizationErrors"
                  data-test-id="state-org-select"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <label for="countryOfOrganization" class="required" data-test-id="country-org-label">
                  Country of Organization
                </label>
                <v-select
                  v-model="entityInfo.countryOfOrganization"
                  :items="countries"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.entityInfo.countryOfOrganization.$touch()"
                  :error-messages="countryOfOrganizationErrors"
                  data-test-id="country-org-select"
                />
              </v-col>
            </v-row>
            
            <!-- Entity Address -->
            <h3 class="mt-6 mb-4">Entity Address</h3>
            
            <label class="required mb-2 d-block" data-test-id="address-same-label">
              Is the entity address the same as the individual's address?
            </label>
            <v-radio-group
              v-model="entityInfo.addressSameAsOwner"
              :error-messages="addressSameAsOwnerErrors"
              hide-details="auto"
              row
              @change="$v.entityInfo.addressSameAsOwner.$touch()"
              data-test-id="address-same-radio-group"
              class="mt-1 mb-4"
            >
              <v-radio
                :ripple="false"
                label="Yes"
                :value="true"
                data-test-id="address-same-yes"
              ></v-radio>
              <v-radio
                :ripple="false"
                label="No"
                :value="false"
                data-test-id="address-same-no"
                class="ml-4"
              ></v-radio>
            </v-radio-group>
            
            <!-- Business Address (conditional) -->
            <v-expand-transition>
              <div v-if="entityInfo.addressSameAsOwner === false">
                <v-row class="mt-6">
                  <v-col cols="12">
                    <h3 class="mb-4">Business Address</h3>
                  </v-col>
                </v-row>
                <label for="street" class="required" data-test-id="street-label">
                  Street Address
                </label>
                <v-text-field
                  outlined
                  type="text"
                  maxlength="256"
                  name="street"
                  aria-required="true"
                  v-model.trim="entityInfo.businessAddress.street"
                  :backgroundColor="inputBackgroundColor"
                  @input="$v.entityInfo.businessAddress.street.$touch()"
                  @blur="$v.entityInfo.businessAddress.street.$touch()"
                  :error-messages="businessAddressStreetErrors"
                  hide-details="auto"
                  class="mb-4"
                  data-test-id="street-input"
                />
                
                <v-row class="mt-2">
                  <v-col cols="12" sm="6">
                    <label for="city" class="required" data-test-id="city-label">
                      City
                    </label>
                    <v-text-field
                      outlined
                      type="text"
                      maxlength="256"
                      name="city"
                      aria-required="true"
                      v-model.trim="entityInfo.businessAddress.city"
                      :backgroundColor="inputBackgroundColor"
                      @input="$v.entityInfo.businessAddress.city.$touch()"
                      @blur="$v.entityInfo.businessAddress.city.$touch()"
                      :error-messages="businessAddressCityErrors"
                      hide-details="auto"
                      data-test-id="city-input"
                    />
                  </v-col>
                  <v-col cols="12" sm="3">
                    <label for="state" class="required" data-test-id="state-label">
                      State
                    </label>
                    <v-select
                      v-model="entityInfo.businessAddress.state"
                      :items="states"
                      :backgroundColor="inputBackgroundColor"
                      outlined
                      dense
                      hide-details="auto"
                      @change="$v.entityInfo.businessAddress.state.$touch()"
                      :error-messages="businessAddressStateErrors"
                      data-test-id="state-select"
                    />
                  </v-col>
                  <v-col cols="12" sm="3">
                    <label for="zipCode" class="required" data-test-id="zip-label">
                      ZIP Code
                    </label>
                    <v-text-field
                      outlined
                      type="text"
                      maxlength="10"
                      name="zipCode"
                      aria-required="true"
                      v-model="entityInfo.businessAddress.zipCode"
                      :backgroundColor="inputBackgroundColor"
                      @input="formatZipCode"
                      @blur="$v.entityInfo.businessAddress.zipCode.$touch()"
                      :error-messages="businessAddressZipErrors"
                      hide-details="auto"
                      data-test-id="zip-input"
                    />
                  </v-col>
                </v-row>
                
                <v-row class="mt-2">
                  <v-col cols="12" sm="6">
                    <label for="country" class="required" data-test-id="country-label">
                      Country
                    </label>
                    <v-select
                      v-model="entityInfo.businessAddress.country"
                      :items="countries"
                      :backgroundColor="inputBackgroundColor"
                      outlined
                      dense
                      hide-details="auto"
                      @change="$v.entityInfo.businessAddress.country.$touch()"
                      :error-messages="businessAddressCountryErrors"
                      data-test-id="country-select"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
            
            
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="entity-info-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, INPUT_DATE_FMT, STATES, COUNTRIES } from '@/common/constants';
import { required, requiredIf, email } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'EntityInfo',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],
  
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    entityInfo: {
      fullLegalName: null,
      email: null,
      phone: null,
      taxIdType: null,
      taxId: null,
      addressSameAsOwner: null,
      businessAddress: {
        street: null,
        city: null,
        state: null,
        zipCode: null,
        country: 'US',
      },
      stateOfOrganization: null,
      countryOfOrganization: 'US',
      taxClassification: null,
    },
    taxIdTypes: [
      { text: 'SSN', value: 'SSN' },
      { text: 'TIN', value: 'TIN' },
    ],
    taxClassifications: [
      { text: 'Corporation – S', value: 'Corporation – S' },
      { text: 'Corporation – C', value: 'Corporation – C' },
      { text: 'General Partnership', value: 'General Partnership' },
      { text: 'Limited Partnership', value: 'Limited Partnership' },
      { text: 'LLC – S', value: 'LLC – S' },
      { text: 'LLC – P', value: 'LLC – P' },
      { text: 'LLC – Partnership', value: 'LLC – Partnership' },
      { text: 'Unincorporated Organization', value: 'Unincorporated Organization' },
    ],
    states: STATES,
    countries: COUNTRIES,
    MENUS: MENUS,
    INPUT_DATE_FMT: INPUT_DATE_FMT,
  }),
  
  validations() {
    const validations = {
      entityInfo: {
        fullLegalName: { required },
        email: { required, email },
        phone: {},
        taxIdType: { required },
        taxId: { 
          required,
          validTaxId(value) {
            if (!value) return true;
            const cleaned = value.replace(/[^0-9]/g, '');
            if (this.entityInfo.taxIdType === 'SSN') {
              return cleaned.length === 9;
            } else if (this.entityInfo.taxIdType === 'TIN') {
              return cleaned.length === 9;
            }
            return false;
          }
        },
        addressSameAsOwner: { required },
        businessAddress: {
          street: { 
            required: requiredIf(function() {
              return this.entityInfo.addressSameAsOwner === false;
            })
          },
          city: { 
            required: requiredIf(function() {
              return this.entityInfo.addressSameAsOwner === false;
            })
          },
          state: { 
            required: requiredIf(function() {
              return this.entityInfo.addressSameAsOwner === false;
            })
          },
          zipCode: { 
            required: requiredIf(function() {
              return this.entityInfo.addressSameAsOwner === false;
            }),
            validZip(value) {
              if (!value || this.entityInfo.addressSameAsOwner !== false) return true;
              const cleaned = value.replace(/[^0-9]/g, '');
              return cleaned.length === 5 || cleaned.length === 9;
            }
          },
          country: { 
            required: requiredIf(function() {
              return this.entityInfo.addressSameAsOwner === false;
            })
          },
        },
        stateOfOrganization: { required },
        countryOfOrganization: { required },
        taxClassification: { required },
      },
      validationGroup: ['entityInfo'],
    };
    
    return validations;
  },
  
  computed: {
    instance() {
      return {
        name: this.name,
        label: this.label,
      };
    },
    
    isEntityInfoComplete() {
      const page = this.getPage(MENUS.CLIENT.ENTITY_INFO.id, this.instance);
      return page && page.lastCompleted;
    },
    
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.ENTITY_INFO.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    
    helpTitle() {
      return 'What does this mean?';
    },
    
    // Error messages
    fullLegalNameErrors() {
      const errors = [];
      if (!this.$v.entityInfo.fullLegalName.$dirty) return errors;
      !this.$v.entityInfo.fullLegalName.required && errors.push('Full legal name is required');
      return errors;
    },
    
    emailErrors() {
      const errors = [];
      if (!this.$v.entityInfo.email.$dirty) return errors;
      !this.$v.entityInfo.email.required && errors.push('Email is required');
      !this.$v.entityInfo.email.email && errors.push('Please enter a valid email');
      return errors;
    },
    
    taxIdTypeErrors() {
      const errors = [];
      if (!this.$v.entityInfo.taxIdType.$dirty) return errors;
      !this.$v.entityInfo.taxIdType.required && errors.push('Tax ID type is required');
      return errors;
    },
    
    taxIdErrors() {
      const errors = [];
      if (!this.$v.entityInfo.taxId.$dirty) return errors;
      !this.$v.entityInfo.taxId.required && errors.push('Tax ID is required');
      !this.$v.entityInfo.taxId.validTaxId && errors.push('Please enter a valid tax ID');
      return errors;
    },
    
    addressSameAsOwnerErrors() {
      const errors = [];
      if (!this.$v.entityInfo.addressSameAsOwner.$dirty) return errors;
      !this.$v.entityInfo.addressSameAsOwner.required && errors.push('This field is required');
      return errors;
    },
    
    businessAddressStreetErrors() {
      const errors = [];
      if (!this.$v.entityInfo.businessAddress.street.$dirty) return errors;
      !this.$v.entityInfo.businessAddress.street.required && errors.push('Street address is required');
      return errors;
    },
    
    businessAddressCityErrors() {
      const errors = [];
      if (!this.$v.entityInfo.businessAddress.city.$dirty) return errors;
      !this.$v.entityInfo.businessAddress.city.required && errors.push('City is required');
      return errors;
    },
    
    businessAddressStateErrors() {
      const errors = [];
      if (!this.$v.entityInfo.businessAddress.state.$dirty) return errors;
      !this.$v.entityInfo.businessAddress.state.required && errors.push('State is required');
      return errors;
    },
    
    businessAddressZipErrors() {
      const errors = [];
      if (!this.$v.entityInfo.businessAddress.zipCode.$dirty) return errors;
      !this.$v.entityInfo.businessAddress.zipCode.required && errors.push('ZIP code is required');
      !this.$v.entityInfo.businessAddress.zipCode.validZip && errors.push('ZIP code must be 5 or 9 digits');
      return errors;
    },
    
    businessAddressCountryErrors() {
      const errors = [];
      if (!this.$v.entityInfo.businessAddress.country.$dirty) return errors;
      !this.$v.entityInfo.businessAddress.country.required && errors.push('Country is required');
      return errors;
    },
    
    
    stateOfOrganizationErrors() {
      const errors = [];
      if (!this.$v.entityInfo.stateOfOrganization.$dirty) return errors;
      !this.$v.entityInfo.stateOfOrganization.required && errors.push('State of organization is required');
      return errors;
    },
    
    countryOfOrganizationErrors() {
      const errors = [];
      if (!this.$v.entityInfo.countryOfOrganization.$dirty) return errors;
      !this.$v.entityInfo.countryOfOrganization.required && errors.push('Country of organization is required');
      return errors;
    },
    
    taxClassificationErrors() {
      const errors = [];
      if (!this.$v.entityInfo.taxClassification.$dirty) return errors;
      !this.$v.entityInfo.taxClassification.required && errors.push('Tax classification is required');
      return errors;
    },
  },
  
  methods: {
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      // Transform the data to match backend expectations
      const entityInfoData = {
        fullLegalName: this.entityInfo.fullLegalName,
        email: this.entityInfo.email,
        phone: this.entityInfo.phone,
        taxIdType: this.entityInfo.taxIdType,
        taxId: this.entityInfo.taxId,
        addressSameAsOwner: this.entityInfo.addressSameAsOwner,
        taxClassification: this.entityInfo.taxClassification,
      };
      
      // Only include businessAddress if addressSameAsOwner is false
      if (this.entityInfo.addressSameAsOwner === false) {
        entityInfoData.businessAddress = {
          line1: this.entityInfo.businessAddress.street,
          city: this.entityInfo.businessAddress.city,
          state: this.entityInfo.businessAddress.state,
          zip: this.entityInfo.businessAddress.zipCode,
          country: this.entityInfo.businessAddress.country,
        };
      }
      
      
      // Include state and country of organization
      entityInfoData.stateOfOrganization = this.entityInfo.stateOfOrganization;
      entityInfoData.countryOfOrganization = this.entityInfo.countryOfOrganization;

      const data = {
        instance: this.instance,
        entityInfo: entityInfoData,
      };

      this.markPageAsSyncing(MENUS.CLIENT.ENTITY_INFO.id, this.instance);

      this.completeStep(MENUS.CLIENT.ENTITY_INFO.id, data, this.instance)
        .then(() => {
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    skip() {
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    toggleFormState(hide) {
      this.formHidden = hide;
      
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    formatPhone() {
      let value = this.entityInfo.phone;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ###-###-####
      if (value.length > 3 && value.length <= 6) {
        value = value.slice(0, 3) + '-' + value.slice(3);
      } else if (value.length > 6) {
        value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
      }
      
      this.entityInfo.phone = value;
    },
    
    formatZipCode() {
      let value = this.entityInfo.businessAddress.zipCode;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ##### or #####-####
      if (value.length > 5) {
        value = value.slice(0, 5) + '-' + value.slice(5, 9);
      }
      
      this.entityInfo.businessAddress.zipCode = value;
      this.$v.entityInfo.businessAddress.zipCode.$touch();
    },
    
    formatTaxId() {
      let value = this.entityInfo.taxId;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format based on type
      if (this.entityInfo.taxIdType === 'SSN') {
        // Format as ###-##-####
        if (value.length > 3 && value.length <= 5) {
          value = value.slice(0, 3) + '-' + value.slice(3);
        } else if (value.length > 5) {
          value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5, 9);
        }
      } else if (this.entityInfo.taxIdType === 'TIN') {
        // Format as ##-#######
        if (value.length > 2) {
          value = value.slice(0, 2) + '-' + value.slice(2, 9);
        }
      }
      
      this.entityInfo.taxId = value;
      this.$v.entityInfo.taxId.$touch();
    },
    
    
    handleSyncStatusChange() {
      if (this.isEntityInfoComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    
    isEntityInfoComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    // Get the current page index based on name and instance
    this.pages = this.interviewPages.getInterviewPages();
    
    // Find the index of this specific entity info page
    this.pageIndex = this.pages.findIndex(page => {
      if (typeof page === 'object' && page.name === MENUS.CLIENT.ENTITY_INFO.id) {
        return page.params && page.params.name === this.name && page.params.label === this.label;
      }
      return false;
    });

    this.formHidden = this.isEntityInfoComplete;
    
    // Load existing data if available
    const existingPage = this.getPage(MENUS.CLIENT.ENTITY_INFO.id, this.instance);
    if (existingPage && existingPage.data && existingPage.data.entityInfo) {
      this.entityInfo = { ...this.entityInfo, ...existingPage.data.entityInfo };
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.isEntityInfoComplete) {
        this.formHidden = true;
      }
    });
    
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>

<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
